<template>
  <Menu
    v-if="state"
    v-model="isActive"
    class="refinement-tree"
    anchor="top left"
    self="top right"
    :offset="[0, 0]"
    :auto-close="false"
    no-route-dismiss
  >
    <div class="refinement-tree--inner">
      <div class="refinement-tree--header">
        <q-toolbar>
          <q-input
            v-model="searchQuery"
            class="refinement-tree--input"
            type="search"
            name="search-input"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            :placeholder="t('filters:Filter.atc.select.placeholderFilterable')"
            clear-icon="mono-cross"
            autofocus
            clearable
            hide-bottom-space
            dense
            no-error-icon
          />
        </q-toolbar>
      </div>

      <div class="refinement-tree--body">
        <div
          v-if="!displayItems.length"
          class="refinement-tree--empty"
        >
          No results.
        </div>

        <RefinementTreeList
          v-else
          :items="displayItems"
          :refine="refine"
        />
      </div>

      <div class="refinement-tree--footer">
        <q-toolbar>
          <ClearRefinements>
            <template #default="scope">
              <Button
                size="sm"
                variant="text"
                start-icon="mono-delete"
                :label="t('filters:Tools.clear.label')"
                :disabled="!scope.canRefine"
                @click.prevent="scope.refine"
              />
            </template>
          </ClearRefinements>
        </q-toolbar>
      </div>
    </div>
  </Menu>
</template>

<script setup>
import { inject, ref, computed, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import RefinementTreeList from './RefinementTreeList.vue'

defineOptions({ name: 'RefinementTree' })
const isActive = defineModel({ type: Boolean, default: false })
const hasSelected = defineModel('hasSelected', { type: Boolean, default: false })
const props = defineProps({
  // Rendered refinementList connector state, owned by a caller-managed widget
  // (e.g. FiltersMenu's permanent one) — RefinementTree never mounts its own
  // widget so it can't fight another widget refining the same attribute.
  state: { type: Object, default: null },
  atcTree: { type: Array, default: () => [] }
})

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })
const { t } = useTranslation()

const isAnyRefined = computed(() => props.state?.items?.some(i => i.isRefined) ?? false)

const searchQuery = ref('')

const prunedTree = computed(() => {
  if (!isActive.value) return []

  const countMap = new Map()
  const refinedSet = new Set()
  for (const item of (props.state?.items ?? [])) {
    countMap.set(item.value, item.count)
    if (item.isRefined) refinedSet.add(item.value)
  }

  return buildFromTree(props.atcTree, countMap, refinedSet)
})

// A node's count already rolls up every descendant's matches (atc_paths indexes the
// full ancestor chain), so a 0-count node can never have a non-zero descendant — safe
// to prune it and its whole subtree. The one exception is a refined node whose count
// dropped to 0 because other active filters/query no longer match it: it's kept (and
// so are its ancestors, so it stays reachable in the nested menu) and flagged
// `isStale` so the UI can show the user it no longer contributes to the results.
const buildFromTree = (nodes, countMap, refinedSet) => nodes
  .map(node => {
    const children = node.children?.length ? buildFromTree(node.children, countMap, refinedSet) : []
    const count = countMap.get(node.path) ?? 0
    const isRefined = refinedSet.has(node.path)

    if (count === 0 && !isRefined && !children.length) return null

    return {
      value: node.path,
      code: node.code,
      label: node.name,
      count,
      isRefined,
      isStale: count === 0 && isRefined,
      children
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.code.localeCompare(b.code))

// Quasar's clearable q-input sets the model to null (not ''), so guard the trim.
const normalizedQuery = computed(() => (searchQuery.value ?? '').trim().toLowerCase())

// Code is prefix-matched (codes are hierarchically prefixed, e.g. "M01" naturally
// means "everything under M01"); name is substring-matched since it's free text.
const matchesQuery = (node, query) =>
  node.code.toLowerCase().startsWith(query) || node.label.toLowerCase().includes(query)

// Walks the already count-pruned tree and collects every matching node at any depth
// into a flat list (children stripped so RefinementTreeList renders each as a
// directly-clickable row instead of a nested drill-down menu).
const flattenMatches = (nodes, query, acc = []) => {
  for (const node of nodes) {
    if (matchesQuery(node, query)) acc.push({ ...node, children: [] })
    if (node.children?.length) flattenMatches(node.children, query, acc)
  }
  return acc
}

// Without an active main search narrowing the tree, most of the ~6,700-code taxonomy
// can have a non-zero count (e.g. a single-letter query can match 1,000+ nodes).
// RefinementTreeList renders this via q-virtual-scroll, so an arbitrarily large
// match list stays cheap — only the rows actually scrolled into view get mounted.
const displayItems = computed(() => {
  if (!normalizedQuery.value) return prunedTree.value
  return flattenMatches(prunedTree.value, normalizedQuery.value).sort((a, b) => a.code.localeCompare(b.code))
})

const refine = value => props.state?.refine(value)

watch(isAnyRefined, val => {
  hasSelected.value = val
  $emitter.emit('PANEL_CHANGE_EVENT', !!props.state?.canRefine)
}, { immediate: true })
</script>

<style>
@reference "#app-styles.css";

.refinement-tree { @apply overflow-visible; }
</style>

<style scoped>
@reference "#app-styles.css";

.refinement-tree--inner {
  @apply flex flex-col min-w-70;

  .refinement-tree--header,
  .refinement-tree--footer {
    @apply shrink h-11 edge-float;

    .q-toolbar { @apply h-full px-4; }
  }

  .refinement-tree--header {
    @apply border-b;

    .refinement-tree--input {
      @apply w-full;

      &:deep(.q-field__control)::before { @apply border-none; }
      &:deep(.q-field__control)::after { @apply content-none; }
    }
  }

  .refinement-tree--footer { @apply border-t; }

  .refinement-tree--body {
    @apply grow;

    .refinement-tree--empty {
      @apply px-4 py-3 text-sm ink-soft;
    }
  }
}
</style>
